import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { isAdmin } from '../../../../../services/AccountService'
import { PaginationStoreType } from '../../../../../stores/NewPaginationStore'
import tableHeadCellStyles from '../../../../../utils/components/CustomTable/styles/tableHeadCellStyles'
import { getStyles } from '../../../../../utils/utils/commonUtils'
import { RequestType } from '../../../../SingleRequestPage/types/Request.type'
import { newRequestDrawerStore } from '../../../stores/NewRequestDrawerStore'
import { getMapHeadTitles, sortedKeys } from '../consts/table.consts'
import { getSortKey } from '../NewRequestsTable'
import { newRequestsSwitcherStore } from '../stores/new-requests-table.stores'
import { RequestTableParamsType } from '../types/new-requests-table.types'
import { useRequestsTableData } from './useNewRequestsTableData'

export const useNewRequestsTable = (
  paginationStore: PaginationStoreType<RequestTableParamsType>,
  checkboxStore: any,
) => {
  // renderTable(false) имитирует isLoading состояние в случае когда данные беруться из кеша tanstackQuery.
  // Без renderTable(false) таблица при долгой отрисовке данных выглядит зависающей
  const navigate = useNavigate()
  const [renderTable, setRenderTable] = useState(false)
  const { data, isLoading, isFetching } = useRequestsTableData(paginationStore)
  const { scrollHeight, sort, setSort, selectedRow } = paginationStore

  const cellClasses = getStyles(tableHeadCellStyles, data)

  const isSmallLength = (data?.content?.length ?? 0) < 50

  useEffect(() => {
    window.scrollTo(0, 0)

    let timeoutId: NodeJS.Timeout
    let timeoutId2: NodeJS.Timeout
    if (!isLoading && !isFetching) {
      timeoutId = setTimeout(() => {
        setRenderTable(true)
      }, 0)
      timeoutId2 = setTimeout(() => {
        checkboxStore.setForbidResetSelectedIds(false)
      }, 100)
    }

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(timeoutId2)
      if (!checkboxStore.forbidResetSelectedIds) checkboxStore.resetAll(true)
    }
  }, [])

  useEffect(() => {
    if (data?.content.length || (!isLoading && !isFetching)) {
      setRenderTable(true)
      if (data?.content.length && !checkboxStore.forbidResetSelectedIds) {
        const allIds = data.content
          .filter((el) => (isAdmin() ? [1, 2, 4].includes(el.status) : true))
          .map((el) => el.id)

        const allSelected = checkboxStore.selectedIds.every((id: number) =>
          allIds.includes(id),
        )
        // чтобы не сбрасывались чекбоксы при изменение размера страницы
        if (checkboxStore.selectedIds.length && allSelected) {
          const newAllIds = data.content
            .filter((el) => el.status === checkboxStore.status)
            .map((el) => el.id)

          checkboxStore.updateSelectedIds(newAllIds, checkboxStore.selectedIds)
        } else {
          checkboxStore.setAllIds(allIds, true)
        }
      } else if (
        checkboxStore.allIds.length &&
        !checkboxStore.forbidResetSelectedIds
      ) {
        checkboxStore.resetAll(true)
      }

      if (scrollHeight) {
        setTimeout(() => {
          window.scrollTo(0, scrollHeight)
        }, 0)
      }
    }
  }, [data, renderTable, isLoading, isFetching])

  const getOnSort = (key: string) =>
    sortedKeys.includes(key)
      ? () => {
          setRenderTable(false)
          setSort(`-${getSortKey(key).toLowerCase()}`)
        }
      : undefined

  const onRowClick = (el: RequestType) => {
    if (isAdmin()) {
      if (checkboxStore.selectedIds.length) {
        checkboxStore.setForbidResetSelectedIds(true)
      }
      newRequestsSwitcherStore.setAllIds(data?.content.map((el) => el.id) ?? [])
      newRequestsSwitcherStore.setCurrId(el.id)
      newRequestsSwitcherStore.setBackRoute('/requests')
      newRequestsSwitcherStore.setPaginationStore(paginationStore)
      navigate(`/requests/${el.id}`)
      paginationStore.setSelectedRow(el, window.scrollY)
      return
    }

    newRequestDrawerStore.openDrawer(true, el)
    paginationStore.setSelectedRow(el, window.scrollY)
  }

  const columns = Object.keys(getMapHeadTitles())

  return useMemo(
    () => ({
      cellClasses,
      isSmallLength,
      setRenderTable,
      isLoading: isLoading || isFetching || !renderTable,
      data,
      sort,
      getOnSort,
      selectedRow,
      columns,
      onRowClick,
    }),
    [cellClasses, isSmallLength, isLoading, data, sort, selectedRow, columns],
  )
}
